import { useEffect, useState } from "react";
import type { AttendeeNotificationSetting, AttendeePushConfig, AttendeeSession } from "@verity-connect/contracts";
import { readClientId } from "./personal";

type PushState =
  | { status: "signed_out" | "loading" | "unavailable" | "off" | "on" | "blocked" }
  | { status: "busy"; action: "enable" | "disable" }
  | { status: "error"; message: string };

export function usePushNotifications(session: AttendeeSession | null, eventSlug: string) {
  const configPath = `/api/attendee/events/${encodeURIComponent(eventSlug)}/push/config`;
  const statePath = `/api/attendee/events/${encodeURIComponent(eventSlug)}/push/state`;
  const subscriptionPath = `/api/attendee/events/${encodeURIComponent(eventSlug)}/push/subscription`;
  const supported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [state, setState] = useState<PushState>({ status: session ? "loading" : "signed_out" });
  const [publicKey, setPublicKey] = useState<string | null>(null);

  useEffect(() => {
    setPublicKey(null);
    if (!session) { setState({ status: "signed_out" }); return; }
    if (!supported) { setState({ status: "unavailable" }); return; }
    if (Notification.permission === "denied") { setState({ status: "blocked" }); return; }
    const controller = new AbortController();
    setState({ status: "loading" });
    void fetch(`${configPath}?clientId=${encodeURIComponent(readClientId())}`, { credentials: "same-origin", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error(await responseMessage(response));
        const config = await response.json() as AttendeePushConfig;
        setPublicKey(config.publicKey);
        setState({ status: config.configured ? (config.subscribed ? "on" : "off") : "unavailable" });
      })
      .catch((reason: unknown) => { if (!controller.signal.aborted) setState({ status: "error", message: message(reason) }); });
    return () => controller.abort();
  }, [configPath, session?.identityId, supported]);

  const enable = async () => {
    if (!session || !publicKey || !supported) return;
    setState({ status: "busy", action: "enable" });
    try {
      const permission = await Notification.requestPermission();
      // Reported the moment it changes rather than on the next app open, because the two
      // answers worth knowing are the ones that stop this function: a person who was asked
      // and said no leaves no subscription anywhere to notice them by.
      void report(statePath, settingNow(supported));
      if (permission !== "granted") { setState({ status: permission === "denied" ? "blocked" : "off" }); return; }
      const registration = await navigator.serviceWorker.register("/sw.js");
      const ready = await navigator.serviceWorker.ready;
      const subscription = await ready.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlBytes(publicKey),
      });
      const serialized = subscription.toJSON();
      if (!serialized.endpoint || !serialized.keys?.p256dh || !serialized.keys.auth) throw new Error("This browser did not provide a complete push subscription.");
      const response = await fetch(subscriptionPath, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ clientId: readClientId(), endpoint: serialized.endpoint, expirationTime: serialized.expirationTime ?? null, keys: serialized.keys }),
      });
      if (!response.ok) { await subscription.unsubscribe(); throw new Error(await responseMessage(response)); }
      setState({ status: "on" });
      void registration.update();
    } catch (reason) { setState({ status: "error", message: message(reason) }); }
  };

  const disable = async () => {
    if (!session || !supported) return;
    setState({ status: "busy", action: "disable" });
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      await (await registration?.pushManager.getSubscription())?.unsubscribe();
      const response = await fetch(subscriptionPath, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ clientId: readClientId() }),
      });
      if (!response.ok) throw new Error(await responseMessage(response));
      setState({ status: "off" });
    } catch (reason) { setState({ status: "error", message: message(reason) }); }
  };

  return { state, enable, disable };
}

/**
 * What this browser would answer if the board asked it directly.
 *
 * "default" is the browser's word for never having been asked, and it is the one worth
 * separating out: somebody who has not been asked is one prompt away from reachable, and
 * somebody who said no is a person to speak to at the door. Everything else in this product
 * sees both of them as silence.
 */
function settingNow(supported: boolean): AttendeeNotificationSetting {
  if (!supported) return "unsupported";
  if (Notification.permission === "granted") return "allowed";
  if (Notification.permission === "denied") return "refused";
  return "not_asked";
}

/**
 * Telling the server, and never letting it matter.
 *
 * A report that fails changes nothing a person can see, so it is swallowed. The alternative
 * — an error on the notifications screen because a count did not save — would be a worse
 * convention than not knowing the number.
 */
async function report(path: string, setting: AttendeeNotificationSetting): Promise<void> {
  try {
    await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ clientId: readClientId(), platform: "web", setting }),
    });
  } catch {
    // Nothing. The person is using the convention; we simply cannot count this one.
  }
}

function base64UrlBytes(value: string): Uint8Array<ArrayBuffer> {
  const padded = `${value}${"=".repeat((4 - value.length % 4) % 4)}`.replace(/-/gu, "+").replace(/_/gu, "/");
  const bytes = Uint8Array.from(atob(padded), (character) => character.charCodeAt(0));
  return new Uint8Array(bytes.buffer);
}
async function responseMessage(response: Response): Promise<string> { const body = await response.json().catch(() => null) as { message?: unknown } | null; return typeof body?.message === "string" ? body.message : "Notification settings could not be changed."; }
function message(reason: unknown): string { return reason instanceof Error ? reason.message : "Notification settings could not be changed."; }
